/**
 * Closed page-type taxonomy for marketing analytics (spec: marketing-cta-capture).
 *
 * The lesson from `page_location` (hand-set per call site, wrong on 100% of production events):
 * a grouping dimension must be TYPED — a closed union, not free-form strings — but resolved in
 * exactly ONE place from the pathname, never passed by callers. This file is that one place.
 *
 * A new page family shows up as `other` (with a dev warning) until someone classifies it here —
 * one mapping entry, no call-site changes anywhere.
 */

export const PAGE_TYPES = [
    'home',
    'pricing',
    'd2c',
    'agency',
    'ai_ads',
    'compare',
    'alternatives',
    'case_study',
    'news',
    'integrations',
    'formats',
    'products',
    'partner_program',
    'affiliate',
    /** One-off campaign/SEO landers (base44, lovable, masterclass, static-video-ads, …) */
    'campaign_lander',
    'other',
] as const

export type IPageType = (typeof PAGE_TYPES)[number]

/** First URL segment → page type. `''` is the homepage. */
const PAGE_TYPE_BY_FIRST_SEGMENT: Record<string, IPageType> = {
    '': 'home',
    pricing: 'pricing',
    d2c: 'd2c',
    groups: 'agency',
    'ai-ads': 'ai_ads',
    compare: 'compare',
    alternatives: 'alternatives',
    'case-study': 'case_study',
    news: 'news',
    integrations: 'integrations',
    formats: 'formats',
    products: 'products',
    'partner-program': 'partner_program',
    affiliate: 'affiliate',
    'ad-analysis': 'campaign_lander',
    'ad-research': 'campaign_lander',
    'agent-prompt-library': 'campaign_lander',
    'agentic-ads-playbook': 'campaign_lander',
    base44: 'campaign_lander',
    'creative-strategist': 'campaign_lander',
    gd: 'campaign_lander',
    gtm: 'campaign_lander',
    lovable: 'campaign_lander',
    manus: 'campaign_lander',
    masterclass: 'campaign_lander',
    'media-buying': 'campaign_lander',
    meta: 'campaign_lander',
    'shopify-store': 'campaign_lander',
    'static-video-ads': 'campaign_lander',
}

export function resolvePageType(pathname: string): IPageType {
    const firstSegment = pathname.split('/')[1] ?? ''
    const pageType = PAGE_TYPE_BY_FIRST_SEGMENT[firstSegment]

    if (pageType === undefined && process.env.NODE_ENV === 'development') {
        console.warn(
            `[page-types] Unmapped page family "/${firstSegment}" → page_type 'other'. ` +
                'Classify it in features/landing-page/tracking/page-types.ts.'
        )
    }

    return pageType ?? 'other'
}
